import { storage, db } from '../firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';

class ImageService {
  constructor() {
    // Simple in-memory cache for URLs to avoid repeated Storage calls
    this.urlCache = new Map();
    
    // Security: File size limits (in bytes)
    this.MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
    this.MAX_TOTAL_IMAGES_SIZE = 50 * 1024 * 1024; // 50MB total per note
    
    // Allowed MIME types
    this.ALLOWED_MIME_TYPES = [
      'image/jpeg',
      'image/jpg', 
      'image/png',
      'image/webp',
      'image/gif'
    ];
  }
  
  /**
   * Validates an image file before processing
   * @param {File} file - The image file to validate
   * @throws {Error} If validation fails
   */
  validateImageFile(file) {
    if (!file) {
      throw new Error('No file provided');
    }

    // Check if it's actually a File object
    if (!(file instanceof File || file instanceof Blob)) {
      throw new Error('Invalid file object');
    }

    // Check file size
    if (file.size > this.MAX_IMAGE_SIZE) {
      const maxSizeMB = (this.MAX_IMAGE_SIZE / 1024 / 1024).toFixed(1);
      throw new Error(`Image must be less than ${maxSizeMB}MB. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
    }

    // Check MIME type
    if (!this.ALLOWED_MIME_TYPES.includes(file.type)) {
      throw new Error(`Invalid file type: ${file.type}. Allowed types: ${this.ALLOWED_MIME_TYPES.join(', ')}`);
    }

    // Check for minimum size (1KB to avoid corrupted files)
    if (file.size < 1024) {
      throw new Error('File is too small. Minimum size is 1KB');
    }
  }
  
  /**
   * Validates multiple image files
   * @param {Array<File>} files - Array of image files
   * @throws {Error} If validation fails
   */
  validateImageFiles(files) {
    if (!Array.isArray(files) || files.length === 0) {
      throw new Error('No files provided');
    }

    // Check total size
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > this.MAX_TOTAL_IMAGES_SIZE) {
      const maxSizeMB = (this.MAX_TOTAL_IMAGES_SIZE / 1024 / 1024).toFixed(1);
      throw new Error(`Total images size must be less than ${maxSizeMB}MB`);
    }

    // Validate each file
    files.forEach((file, index) => {
      try {
        this.validateImageFile(file);
      } catch (error) {
        throw new Error(`File ${index + 1}: ${error.message}`);
      }
    });
  }

  /**
   * Resizes and converts an image file.
   * @param {File} file - The input image file.
   * @param {number} maxDimension - Maximum width or height.
   * @param {string} format - 'image/webp' or 'image/jpeg'.
   * @param {number} quality - Quality between 0 and 1.
   * @returns {Promise<Blob>} - The processed image blob.
   */
  async processImage(file, maxDimension, format, quality) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions maintaining aspect ratio
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Canvas to Blob conversion failed'));
          },
          format,
          quality
        );
      };

      img.onerror = (error) => {
        URL.revokeObjectURL(url);
        reject(error);
      };

      img.src = url;
    });
  }

  /**
   * Uploads a single image (main + thumbnail) to Firebase Storage.
   * @param {File} file - The image file.
   * @param {string} uid - User ID.
   * @returns {Promise<string>} - The generated image UID.
   */
  async uploadImageToStorage(file, uid) {
    // Security: Validate file before processing
    this.validateImageFile(file);
    
    const imageUID = uuidv4();
    
    try {
      // Process Main Image: WebP, 0.8, max 2048px
      const mainImageBlob = await this.processImage(file, 2048, 'image/webp', 0.8);
      const mainImagePath = `imagenotes/${uid}/${imageUID}/image.webp`;
      const mainImageRef = ref(storage, mainImagePath);
      
      // Process Thumbnail: JPEG, 0.85, max 400px
      const thumbnailBlob = await this.processImage(file, 400, 'image/jpeg', 0.85);
      const thumbnailPath = `imagenotes/${uid}/${imageUID}/thumbnail.jpg`;
      const thumbnailRef = ref(storage, thumbnailPath);

      // Upload in parallel
      await Promise.all([
        uploadBytes(mainImageRef, mainImageBlob, { contentType: 'image/webp' }),
        uploadBytes(thumbnailRef, thumbnailBlob, { contentType: 'image/jpeg' })
      ]);

      return imageUID;
    } catch (error) {
      console.error('Error in uploadImageToStorage:', error);
      throw error;
    }
  }

  /**
   * Uploads multiple images and updates the note in Firestore.
   * @param {Array<File>} files - Array of image files.
   * @param {string} noteId - The note ID.
   * @param {string} uid - The user ID.
   * @returns {Promise<Array<string>>} - Array of new image UIDs.
   */
  async uploadImages(files, noteId, uid) {
    // Security: Validate all files before uploading
    this.validateImageFiles(files);
    
    try {
      // Upload all images to Storage
      const uploadPromises = files.map(file => this.uploadImageToStorage(file, uid));
      const newImageUIDs = await Promise.all(uploadPromises);

      // Update Firestore
      const noteRef = doc(db, 'notes', noteId);
      await updateDoc(noteRef, {
        images: arrayUnion(...newImageUIDs)
      });

      return newImageUIDs;
    } catch (error) {
      console.error('Error uploading images:', error);
      throw error;
    }
  }

  /**
   * Deletes an image from Storage and removes it from the note in Firestore.
   * @param {string} noteId - The note ID.
   * @param {string} uid - The user ID.
   * @param {string} imageUID - The image UID to delete.
   */
  async deleteImage(noteId, uid, imageUID) {
    try {
      // Delete from Storage (Main + Thumbnail)
      const mainImageRef = ref(storage, `imagenotes/${uid}/${imageUID}/image.webp`);
      const thumbnailRef = ref(storage, `imagenotes/${uid}/${imageUID}/thumbnail.jpg`);
      
      // Best effort delete - if one fails, try the other, but don't block firestore update strictly?
      // Actually, better to try delete both. Catch errors individually if needed, but for now Promise.allSettled or just Promise.all
      // Note: If file doesn't exist, deleteObject throws. We should handle that gracefully.
      
      const deleteSafe = async (ref) => {
          try {
              await deleteObject(ref);
          } catch (e) {
              console.warn('Failed to delete storage object:', ref.fullPath, e);
              if (e.code !== 'storage/object-not-found') throw e;
          }
      };

      await Promise.all([
        deleteSafe(mainImageRef),
        deleteSafe(thumbnailRef)
      ]);

      // Update Firestore
      const noteRef = doc(db, 'notes', noteId);
      
      // We need to remove the ID from the array. arrayRemove works well.
      await updateDoc(noteRef, {
        images: arrayRemove(imageUID)
      });

      // Clear from cache
      this.urlCache.delete(`${uid}/${imageUID}/thumb`);
      this.urlCache.delete(`${uid}/${imageUID}/full`);

    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  }

  /**
   * Gets the URL for an image (cached).
   * @param {string} uid - User ID.
   * @param {string} imageUID - Image UID.
   * @param {boolean} thumbnail - Whether to get the thumbnail or full image.
   * @returns {Promise<string>} - The download URL.
   */
  async getImageURL(uid, imageUID, thumbnail = true) {
    const cacheKey = `${uid}/${imageUID}/${thumbnail ? 'thumb' : 'full'}`;
    
    if (this.urlCache.has(cacheKey)) {
      return this.urlCache.get(cacheKey);
    }

    try {
      const path = thumbnail 
        ? `imagenotes/${uid}/${imageUID}/thumbnail.jpg`
        : `imagenotes/${uid}/${imageUID}/image.webp`;
      
      const storageRef = ref(storage, path);
      const url = await getDownloadURL(storageRef);
      
      this.urlCache.set(cacheKey, url);
      return url;
    } catch (error) {
      console.error(`Error getting URL for ${thumbnail ? 'thumbnail' : 'image'} ${imageUID}:`, error);
      return null; // Return null on error (e.g. image not found)
    }
  }
}

export const imageService = new ImageService();

